/**
 * StickySection - A Section variant with sticky positioning
 *
 * Use for sections that need to stick to a position while scrolling,
 * like tab headers or navigation bars within the page.
 *
 * Usage:
 * <StickySection top="64px" bg="white">...</StickySection>
 */

import { ReactNode } from "react";
import Box, { BoxProps } from "@ui-kit/layout/Box/Box.web";
import { MaxWidthKey } from "@shared/config/layout.config";
import { PageContentWrapper } from "../PageContentWrapper/PageContentWrapper.web";

export interface StickySectionProps extends BoxProps {
  children?: ReactNode;
  /**
   * Max width for the content wrapper
   * @default "content"
   */
  maxWidth?: MaxWidthKey | string;
  /**
   * Disable horizontal padding
   * @default false
   */
  noPadding?: boolean;
  /**
   * Disable padding on mobile (base breakpoint), apply padding from sm up
   * @default false
   */
  noPaddingMobile?: boolean;
}

export function StickySection({
  maxWidth = "content",
  noPadding = false,
  noPaddingMobile = false,
  children,
  position = "sticky",
  ...props
}: StickySectionProps): JSX.Element {
  return (
    <Box width="100%" position={position} {...props}>
      <PageContentWrapper
        maxWidth={maxWidth}
        noPadding={noPadding}
        noPaddingMobile={noPaddingMobile}
      >
        {children}
      </PageContentWrapper>
    </Box>
  );
}

export default StickySection;
